/**
 * The sections editor's fixed choices (ContentSectionsEditorComponent), kept apart from its logic. Values must match
 * picaluna-v3's zod enums exactly: an unknown stored value aborts the whole site build. The brand palette (Background,
 * Text colour, Panel colours) is in block-colours.ts.
 */

/* Heading level (#259). Values must match picaluna-v3's renderer and the
   server whitelist. H2 is the default and stores nothing (toMap), so
   every existing block keeps rendering exactly as before. */
export const HEADING_LEVEL_OPTIONS = [
  { label: 'H2 (default)', value: 'h2' },
  { label: 'H3', value: 'h3' },
  { label: 'H4', value: 'h4' },
];

/* Alignment of a block's heading (Robin, 2026-10-29). Values must match
   picaluna-v3's renderer; left is the default and stores nothing. */
export const ALIGN_OPTIONS = [
  { label: 'Left (default)', value: 'left' },
  { label: 'Centre', value: 'center' },
  { label: 'Right', value: 'right' },
];

/* The buttons row under a block: CENTRE is the default (Ivan, 2026-10-29), so
   Left is a real choice and is stored. */
export const BUTTON_ALIGN_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Centre', value: 'center' },
  { label: 'Right', value: 'right' },
];

/* The block's own top and bottom padding (Robin, 2026-10-29). "Top and
   Bottom" is the default and stores nothing; with "None" two blocks read as
   one section. */
export const SPACING_OPTIONS = [
  { label: 'Top and Bottom', value: 'both' },
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
  { label: 'None', value: 'none' },
];

/* Where a section's image sits (#239). Values must match picaluna-v3's
   renderer exactly; blank = under everything, which is where every stored
   section already puts it. Additive only — renaming one orphans stored
   sections, which then silently fall back to the bottom. */
export const IMAGE_LAYOUT_OPTIONS = [
  { label: 'Above the heading', value: 'above-heading' },
  { label: 'Between heading and text', value: 'below-heading' },
  { label: 'Left of the text', value: 'left' },
  { label: 'Right of the text', value: 'right' },
  { label: 'Under everything', value: 'bottom' },
];

export const VARIANT_OPTIONS = [
  { label: 'Condensed strip', value: 'condensed' },
  { label: 'Full breakdown', value: 'full' },
];

/**
 * How each footer link renders (#230). These map to the site's CtaButton
 * variants; 'link' is plain underlined text, which is how every section
 * link looked before #230, so it stays the default.
 */
export const LINK_STYLE_OPTIONS = [
  { label: 'Link', value: 'link' },
  { label: 'Button — primary', value: 'primary' },
  { label: 'Button — secondary', value: 'secondary' },
  { label: 'Button — tertiary', value: 'tertiary' },
];

/**
 * Page FAQs display type (#202). "Section" shows the items this block picks;
 * "Full page" shows every FAQ marked "Display on the FAQ page", so the FAQ
 * listing maintains itself as items are added.
 */
/**
 * Places near this suburb (#253): which list. Values must match picaluna-v3's
 * section `variant` enum. The suburb comes from the page's own address.
 */
export const SUBURB_PLACES_OPTIONS = [
  { label: 'Crematoria and cemeteries', value: 'disposal' },
  { label: 'Places for a funeral service or wake', value: 'venues' },
];

export const FAQ_VARIANT_OPTIONS = [
  { label: 'Section (the FAQs picked below)', value: 'section' },
  { label: 'Full page (every FAQ marked for it)', value: 'full' },
];

export const BLOG_VARIANT_OPTIONS = [
  { label: 'Teaser (a few of the newest)', value: 'teaser' },
  { label: 'Full page (with pagination)', value: 'full' },
];
export const GALLERY_VARIANT_OPTIONS = [
  { label: 'Grid', value: 'grid' },
  { label: 'Carousel (swipe)', value: 'carousel' },
];
/* 2–6, matching the zod schema and Panels.astro (#221). Melbourne's "How
   we can help" needs five panels in a row; the cap of 4 here was the only
   thing stopping it, and picking 5 on the site side had been silently
   falling back to 3. */
export const PANEL_COLUMN_OPTIONS = [
  { label: '2 columns', value: 2 },
  { label: '3 columns', value: 3 },
  { label: '4 columns', value: 4 },
  { label: '5 columns', value: 5 },
  { label: '6 columns', value: 6 },
];
/**
 * Directors grid and venues directory go to 6 (#231, #232) — their cards
 * are smaller than a panel, and Robin asked for 3-up on venues because the
 * 2-up default "takes up too much vertical room".
 *
 * Mobile is NOT the author's choice on either: a director card carries a
 * photo, a name and a role, so it stays 1-up; a venue card is a name and a
 * suburb line, so it stays 2-up. Only the desktop count varies.
 */
export const WIDE_COLUMN_OPTIONS = [
  { label: '2 columns', value: 2 },
  { label: '3 columns', value: 3 },
  { label: '4 columns', value: 4 },
  { label: '5 columns', value: 5 },
  { label: '6 columns', value: 6 },
];
export const PANEL_ROW_OPTIONS = [
  { label: '1 row', value: 1 },
  { label: '2 rows', value: 2 },
  { label: '3 rows', value: 3 },
];
